import { Injectable, OnDestroy } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { LaunchContext } from './unified-vm-launcher.service';
import { Log, IShortLogger } from './logger.service';

/**
 * Interface defining the structure of VM session data with enhanced multi-session support.
 * Contains all necessary information to launch and authenticate a VM session.
 *
 * @interface VmSessionData
 */
export interface VmSessionData {
  /** Server-generated encrypted token containing all VM connection details */
  encryptedToken: string;

  /** Cloud username for VM authentication (displayed in sidebar) */
  cloudUserName: string;

  /** Cloud password for VM authentication (displayed in sidebar) */
  password: string;

  /** Class identifier for tracking purposes */
  classId: string;

  /** User identifier - can be current user or target user (for trainer access) */
  userId: string;

  /** Optional target user identifier for trainer remote access scenarios */
  targetUserId?: string;

  /** Timestamp when session was created */
  createdAt: Date;

  /** Connection protocol (vnc or rdp) */
  protocol?: 'vnc' | 'rdp';

  /** Launch context for different VM launch scenarios */
  launchContext?: LaunchContext;

  /** Template identifier for template machine launches */
  templateId?: string;

  /** Resource identifier for specific resource launches */
  resourceId?: string;

  /** Hostname for test connections */
  hostname?: string;
}

/**
 * Service for managing VM session data with in-memory caching and multi-session support.
 *
 * This service replaces the localStorage-based approach with a memory-based solution
 * that supports multiple concurrent VM sessions. It uses composite keys {classId}/{userId}
 * to enable trainers to access multiple students' VMs within the same class without
 * session overwrites.
 *
 * Key features:
 * - In-memory session caching with composite keys
 * - Multi-session support for trainer remote access
 * - Automatic cleanup on service destruction
 * - Session existence checking to avoid unnecessary API calls
 * - Thread-safe session management
 *
 * @class VmSessionManagerService
 *
 * @example
 * ```typescript
 * // Inject in component
 * constructor(private vmSessionManager: VmSessionManagerService) {}
 *
 * // Store session data when launching VM
 * this.vmSessionManager.cacheSession('class-123', 'user-456', {
 *   encryptedToken: 'server-encrypted-token',
 *   cloudUserName: 'azureuser',
 *   password: 'P@ssw0rd123',
 *   classId: 'class-123',
 *   userId: 'user-456'
 * });
 *
 * // Check if session exists before API call
 * if (!this.vmSessionManager.hasSession('class-123', 'user-456')) {
 *   // Make API call to get session data
 * }
 *
 * // Retrieve session data in VM window
 * const sessionData = this.vmSessionManager.getSession('class-123', 'user-456');
 * if (sessionData) {
 *   // Use data to initialize VM connection
 * }
 *
 * // Clean up specific session
 * this.vmSessionManager.clearSession('class-123', 'user-456');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class VmSessionManagerService implements OnDestroy {
  private readonly logger: IShortLogger<VmSessionManagerService> = Log.create(VmSessionManagerService);

  /**
   * Storage key prefix for VM session data in localStorage.
   * Key format: "vm_session_{classId}/{userId}"
   * Using localStorage instead of memory to support cross-window access.
   * @private
   * @type {string}
   */
  private readonly SESSION_PREFIX = 'vm_session_';

  /**
   * Creates an instance of VmSessionManagerService.
   * Automatically sets up cleanup handlers.
   */
  constructor(private localStorage: LocalStorageService) {
    this.logger.d('VmSessionManagerService initialized');
  }

  /**
   * Generate a composite session key from classId and userId with localStorage prefix.
   * This enables multi-session support by creating unique keys for each class/user combination.
   *
   * @param {string} classId - The unique class identifier
   * @param {string} userId - The user identifier (current user or target user for trainers)
   * @returns {string} Storage key in format "vm_session_{classId}/{userId}"
   *
   * @example
   * ```typescript
   * const key = this.generateSessionKey('class-123', 'user-456');
   * // Returns: "vm_session_class-123/user-456"
   * ```
   */
  generateSessionKey(classId: string, userId: string): string {
    return `${this.SESSION_PREFIX}${classId}/${userId}`;
  }

  /**
   * Cache session data with composite key for multi-session support.
   * Automatically adds creation timestamp for session tracking.
   *
   * @param {string} classId - The unique class identifier
   * @param {string} userId - The user identifier
   * @param {VmSessionData} sessionData - The session data to cache
   *
   * @example
   * ```typescript
   * // Regular user session
   * this.cacheSession('class-123', 'current-user-id', sessionData);
   *
   * // Trainer accessing student session
   * this.cacheSession('class-123', 'student-user-id', sessionData);
   * ```
   */
  cacheSession(classId: string, userId: string, sessionData: VmSessionData): void {
    const sessionKey = this.generateSessionKey(classId, userId);

    const enhancedSessionData: VmSessionData = {
      ...sessionData,
      createdAt: new Date()
    };

    this.localStorage.setItem(sessionKey, enhancedSessionData);
    this.logger.d(`Cached VM session with key: ${sessionKey}`, enhancedSessionData);
  }

  /**
   * Retrieve session data using composite key.
   *
   * @param {string} classId - The unique class identifier
   * @param {string} userId - The user identifier
   * @returns {VmSessionData | null} The session data or null if not found
   *
   * @example
   * ```typescript
   * const sessionData = this.getSession('class-123', 'user-456');
   * if (sessionData) {
   *   this.logger.d('Token:', sessionData.encryptedToken);
   *   this.logger.d('Username:', sessionData.cloudUserName);
   * }
   * ```
   */
  getSession(classId: string, userId: string): VmSessionData | null {
    const sessionKey = this.generateSessionKey(classId, userId);
    const sessionData = this.localStorage.getItem<VmSessionData>(sessionKey);

    if (sessionData) {
      // Convert createdAt back to Date object if it exists
      if (sessionData.createdAt) {
        sessionData.createdAt = new Date(sessionData.createdAt);
      }
      this.logger.d(`Retrieved VM session with key: ${sessionKey}`, sessionData);
      return sessionData;
    } else {
      this.logger.w(`No VM session found with key: ${sessionKey}`);
      return null;
    }
  }

  /**
   * Check if a session exists in cache without retrieving it.
   * Useful for determining whether to make API calls.
   *
   * @param {string} classId - The unique class identifier
   * @param {string} userId - The user identifier
   * @returns {boolean} True if session exists, false otherwise
   *
   * @example
   * ```typescript
   * if (!this.hasSession('class-123', 'user-456')) {
   *   // Session doesn't exist, make API call
   *   this.classService.getLaunchDetails(...).subscribe(data => {
   *     this.cacheSession('class-123', 'user-456', data);
   *   });
   * } else {
   *   // Session exists, use cached data
   *   const data = this.getSession('class-123', 'user-456');
   * }
   * ```
   */
  hasSession(classId: string, userId: string): boolean {
    const sessionKey = this.generateSessionKey(classId, userId);
    return this.localStorage.hasItem(sessionKey);
  }

  /**
   * Remove a specific session from cache.
   *
   * @param {string} classId - The unique class identifier
   * @param {string} userId - The user identifier
   *
   * @example
   * ```typescript
   * this.clearSession('class-123', 'user-456');
   * ```
   */
  clearSession(classId: string, userId: string): void {
    const sessionKey = this.generateSessionKey(classId, userId);
    const existed = this.localStorage.hasItem(sessionKey);

    this.localStorage.removeItem(sessionKey);

    if (existed) {
      this.logger.d(`Cleared VM session with key: ${sessionKey}`);
    } else {
      this.logger.w(`No VM session found to clear with key: ${sessionKey}`);
    }
  }

  /**
   * Get all cached session keys for debugging and monitoring.
   *
   * @returns {string[]} Array of all session keys currently in cache
   *
   * @example
   * ```typescript
   * const activeKeys = this.getAllSessionKeys();
   * this.logger.d('Active sessions:', activeKeys.length);
   * ```
   */
  getAllSessionKeys(): string[] {
    const keys: string[] = [];
    // Access the underlying storage since LocalStorageService doesn't expose keys iteration
    const storage = (this.localStorage as any).storage || localStorage;
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(this.SESSION_PREFIX)) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Get the number of currently cached sessions.
   *
   * @returns {number} Number of active sessions in cache
   *
   * @example
   * ```typescript
   * const sessionCount = this.getSessionCount();
   * this.logger.d('Active VM sessions:', sessionCount);
   * ```
   */
  getSessionCount(): number {
    return this.getAllSessionKeys().length;
  }

  /**
   * Clear all sessions from memory cache.
   * This removes all stored VM session data.
   *
   * @example
   * ```typescript
   * this.clearAllSessions();
   * ```
   */
  clearAllSessions(): void {
    const sessionKeys = this.getAllSessionKeys();
    const sessionCount = sessionKeys.length;

    sessionKeys.forEach(key => {
      this.localStorage.removeItem(key);
    });

    this.logger.i(`Cleared all VM sessions. Removed ${sessionCount} sessions.`);
  }

  /**
   * Angular lifecycle hook - cleanup when service is destroyed.
   * Automatically clears all sessions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.logger.d('VmSessionManagerService destroying, clearing all sessions');
    this.clearAllSessions();
  }

  /**
   * Get sessions for a specific class (useful for trainers).
   * Returns all sessions where the classId matches.
   *
   * @param {string} classId - The class identifier
   * @returns {Array<{userId: string, sessionData: VmSessionData}>} Array of sessions for the class
   *
   * @example
   * ```typescript
   * const classSessions = this.getSessionsForClass('class-123');
   * this.logger.d(`Class has ${classSessions.length} active sessions`);
   * ```
   */
  getSessionsForClass(classId: string): Array<{userId: string, sessionData: VmSessionData}> {
    const classSessions: Array<{userId: string, sessionData: VmSessionData}> = [];
    const sessionKeys = this.getAllSessionKeys();

    for (const key of sessionKeys) {
      const sessionData = this.localStorage.getItem<VmSessionData>(key);
      if (sessionData && sessionData.classId === classId) {
        // Extract userId from key format: "vm_session_{classId}-{userId}"
        const compositeKey = key.replace(this.SESSION_PREFIX, '');
        const userId = compositeKey.split('-')[1];
        // Convert createdAt back to Date object
        if (sessionData.createdAt) {
          sessionData.createdAt = new Date(sessionData.createdAt);
        }
        classSessions.push({ userId, sessionData });
      }
    }

    return classSessions;
  }

  /**
   * Clear all sessions for a specific class.
   * Useful when a class ends or needs cleanup.
   *
   * @param {string} classId - The class identifier
   * @returns {number} Number of sessions cleared
   *
   * @example
   * ```typescript
   * const clearedCount = this.clearSessionsForClass('class-123');
   * this.logger.d(`Cleared ${clearedCount} sessions for class`);
   * ```
   */
  clearSessionsForClass(classId: string): number {
    let clearedCount = 0;
    const sessionKeys = this.getAllSessionKeys();

    for (const key of sessionKeys) {
      const sessionData = this.localStorage.getItem<VmSessionData>(key);
      if (sessionData && sessionData.classId === classId) {
        this.localStorage.removeItem(key);
        clearedCount++;
      }
    }

    if (clearedCount > 0) {
      this.logger.i(`Cleared ${clearedCount} VM sessions for class: ${classId}`);
    }

    return clearedCount;
  }
}
